import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Navigation } from "@/components/Navigation";
import { FilterModal } from "@/components/FilterModal";
import {
  Plus,
  MoreVertical,
  Edit,
  ToggleLeft,
  FlaskConical,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
} from "lucide-react";
import {
  useBooks,
  useToggleBookStatus,
  useUpdateBookSimulationEligibility,
} from "@/hooks/api/useBooks";
import { useCategories } from "@/hooks/api/useCategories";
import { useToast } from "@/hooks/use-toast";
import { isHandledError } from "@/lib/api/errors";
import { MATERIALITIES } from "@/data/materialsAndCategories";
import type { BooksListParams } from "@/services/books.service";
import type { BookListItem } from "@/services/books.service";

/** Determina aptidão para simulação a partir do campo retornado pela API se disponível */
function isAptoSimulacao(book: BookListItem): boolean {
  if (typeof book.eligible_for_simulation === "boolean") {
    return book.eligible_for_simulation;
  }
  if (typeof book.eligible_for_simulation === "string") {
    return book.eligible_for_simulation === "apto";
  }
  // Fallback conservador: não apto
  return false;
}

function getEligibilityLabel(value: BookListItem["eligible_for_simulation"]): string {
  if (typeof value === "boolean") {
    return value ? "APTO" : "INAPTO";
  }
  if (typeof value === "string") {
    if (value.toLowerCase() === "incompleto") return "INAPTO";
    return value.toUpperCase();
  }
  return "—";
}

function getEligibilityBadgeClass(value: BookListItem["eligible_for_simulation"]): string {
  if (value === true || value === "apto") {
    return "bg-[#135045] text-[#88C0B2] font-semibold text-xs";
  }
  if (typeof value === "string" && value.toLowerCase() === "incompleto") {
    return "bg-[#E8EFF0] text-[#3B494A] font-semibold text-xs";
  }
  return "bg-[#E8EFF0] text-[#3B494A] font-semibold text-xs";
}

function getBookStatusLabel(status: BookListItem["status"]): string {
  if (status === "active") return "Ativo";
  if (status === "inactive") return "Inativo";
  return "—";
}

function getBookStatusBadgeClass(status: BookListItem["status"]): string {
  if (status === "active") {
    return "bg-[#135045] text-[#88C0B2] font-semibold text-xs";
  }
  if (status === "inactive") {
    return "bg-[#E8EFF0] text-[#3B494A] font-semibold text-xs";
  }
  return "bg-[#E8EFF0] text-[#6B797A] font-semibold text-xs";
}


function HighlightedText({
  text,
  highlight,
}: {
  text: string;
  highlight: string;
}) {
  if (!highlight.trim()) {
    return <span className="text-[#3B494A]">{text}</span>;
  }

  const regex = new RegExp(`(${highlight})`, "gi");
  const parts = text.split(regex);

  return (
    <span className="text-[#3B494A]">
      {parts.map((part, index) =>
        regex.test(part) ? (
          <mark key={index} className="bg-[#50F1FC] text-[#151C1D] px-0">
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </span>
  );
}

interface Filters {
  primaryCategories: string[];
  secondaryCategories: string[];
  materialidades: string[];
  csatMin: number;
  csatMax: number;
  costMin: number;
  costMax: number;
  contractType: ("determinado" | "indeterminado")[];
  contractStatus: ("ativo" | "expirado")[];
  simulationAptness: ("apto" | "inapto")[];
  contractPending: boolean;
  costPending: boolean;
  editorialPending: boolean;
  expiration_from?: string;
  expiration_to?: string;
}

export default function Catalog() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBook, setSelectedBook] = useState<string | null>(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    primaryCategories: [],
    secondaryCategories: [],
    materialidades: [],
    csatMin: 0,
    csatMax: 5,
    costMin: 1,
    costMax: 50,
    contractType: [],
    contractStatus: [],
    simulationAptness: [],
    contractPending: false,
    costPending: false,
    editorialPending: false,
    expiration_from: "",
    expiration_to: "",
  });

  const { data: categories } = useCategories();
  const categoryCodeToId = useMemo(() => {
    const m = new Map<string, number>();
    categories?.forEach((c) => {
      const id = typeof c.id === "string" ? parseInt(c.id, 10) : Number(c.id);
      if (!isNaN(id) && (c.code || c.name))
        m.set((c.code || c.name) as string, id);
    });
    return m;
  }, [categories]);

  const booksListParams = useMemo<BooksListParams>(() => {
    const primaryCategoryIds = filters.primaryCategories
      .map((code) => categoryCodeToId.get(code))
      .filter((id): id is number => id != null);
    const secondaryCategoryIds = filters.secondaryCategories
      .map((code) => categoryCodeToId.get(code))
      .filter((id): id is number => id != null);

    const csatFilterActive = filters.csatMin > 0 || filters.csatMax < 5;
    const params: BooksListParams = {
      page: currentPage,
      per_page: itemsPerPage,
      search: searchQuery.trim() || undefined,
      cost_min: filters.costMin > 1 ? filters.costMin : undefined,
      cost_max: filters.costMax < 50 ? filters.costMax : undefined,
      // Só envia CSAT quando o usuário alterou a faixa; com mínimo 0 usa `null` na API (não o número 0)
      csat_min: csatFilterActive
        ? filters.csatMin === 0
          ? null
          : filters.csatMin
        : undefined,
      csat_max: filters.csatMax < 5 ? filters.csatMax : undefined,
      primary_category_ids: primaryCategoryIds.length
        ? [...new Set(primaryCategoryIds)]
        : undefined,
      secondary_category_ids: secondaryCategoryIds.length
        ? [...new Set(secondaryCategoryIds)]
        : undefined,
    };

    const materialityIds = filters.materialidades.flatMap((name) => {
      if (name === "Capa Dura") return [1];
      if (name === "Brochura") return [2];
      if (name === "Pano") return [3];
      if (name === "Banho") return [4];
      if (name === "Cartonado") {
        // IDs 5 a 22 são tipos de cartonado
        return MATERIALITIES.filter((m) => m.id >= 5 && m.id <= 22).map((m) => m.id);
      }
      return [];
    });

    if (materialityIds.length) {
      params.materiality_ids = materialityIds;
    }

    if (filters.contractType.length === 1)
      params.contract_type =
        filters.contractType[0] === "determinado" ? "determinate" : "indeterminate";
    if (filters.contractStatus.length === 1)
      params.contract_status = filters.contractStatus[0] === "ativo" ? "active" : "expired";
    if (filters.expiration_from) params.expiration_from = filters.expiration_from;
    if (filters.expiration_to) params.expiration_to = filters.expiration_to;
    if (filters.contractPending) params.pending_contract = true;
    if (filters.costPending) params.pending_cost = true;
    if (filters.editorialPending) params.editorial_complete = false;

    // Map simulation filters to simulation_eligible (true/false)
    if (filters.simulationAptness.length === 1) {
      params.simulation_eligible = filters.simulationAptness[0] === "apto";
    }

    return params;
  }, [
    currentPage,
    itemsPerPage,
    searchQuery,
    filters,
    categoryCodeToId,
  ]);

  const { toast } = useToast();
  const { data, isLoading, isError, error } = useBooks(booksListParams);
  const currentBooks: BookListItem[] = Array.isArray(data?.data) ? data.data : [];
  const pagination = data?.pagination;

  const toggleStatusMutation = useToggleBookStatus();
  const updateSimulationEligibilityMutation = useUpdateBookSimulationEligibility();

  const handleToggleStatus = async (e: React.MouseEvent, bookId: number) => {
    e.stopPropagation();
    setSelectedBook(null);
    try {
      await toggleStatusMutation.mutateAsync(bookId);
    } catch (err: unknown) {
      if (!isHandledError(err)) {
        const msg =
          (err as any)?.response?.data?.message ??
          (err as Error)?.message ??
          "Erro ao alterar status do livro.";
        toast({ title: "Erro", description: msg, variant: "destructive" });
      }
    }
  };

  const handleToggleSimulationEligibility = async (
    e: React.MouseEvent,
    book: BookListItem,
  ) => {
    e.stopPropagation();
    setSelectedBook(null);
    try {
      await updateSimulationEligibilityMutation.mutateAsync({
        id: book.id,
        body: { simulation_eligible: !isAptoSimulacao(book) },
      });
    } catch (err: unknown) {
      if (!isHandledError(err)) {
        const msg =
          (err as any)?.response?.data?.message ??
          (err as Error)?.message ??
          "Erro ao alterar aptidão para simulação.";
        toast({ title: "Erro", description: msg, variant: "destructive" });
      }
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters]);

  const clearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  // Debounce search input: 500ms, minimum 3 chars
  useEffect(() => {
    const t = setTimeout(() => {
      const q = (searchInput || "").trim();
      if (q.length >= 3) {
        setSearchQuery(q);
      } else {
        setSearchQuery("");
      }
    }, 500);

    return () => clearTimeout(t);
  }, [searchInput]);

  const toggleQuickFilter = (
    filterType: "contractPending" | "costPending" | "editorialPending",
  ) => {
    setCurrentPage(1);
    setFilters((prev) => ({
      ...prev,
      [filterType]: !prev[filterType],
    }));
  };

  const countActiveFilters = () => {
    let count = 0;
    if (filters.primaryCategories.length > 0) count++;
    if (filters.secondaryCategories.length > 0) count++;
    if (filters.materialidades.length > 0) count++;
    if (filters.csatMin > 0 || filters.csatMax < 5) count++;
    if (filters.costMax < 50) count++;
    if (filters.contractType.length > 0) count++;
    if (filters.contractStatus.length > 0) count++;
    if (filters.simulationAptness.length > 0) count++;
    if (filters.contractPending) count++;
    if (filters.costPending) count++;
    if (filters.editorialPending) count++;
    return count;
  };

  const hasActiveFilters = () => {
    return countActiveFilters() > 0;
  };

  return (
    <div className="flex min-h-screen bg-white">
      <Navigation />
      <FilterModal
        open={isFilterModalOpen}
        onOpenChange={setIsFilterModalOpen}
        filters={filters}
        onFiltersChange={setFilters}
      />

      <main className="flex-1 flex flex-col">
        <div className="border-b border-[#BAC9CA] bg-white">
          <div className="flex items-center justify-between px-5 py-5">
            <h1 className="text-[28px] font-medium text-[#151C1D]">Catálogo</h1>
            <Button
              onClick={() => navigate("/catalog/criar/novo")}
              className="bg-[#00646B] hover:bg-[#00646B]/90 text-white"
            >
              <Plus className="w-5 h-5 mr-2" />
              Cadastrar Novo Livro
            </Button>
          </div>
        </div>

        <div className="flex-1 px-5 py-9">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-5">
            <div className="relative flex-1 max-w-[420px] w-full">
              <div className="relative flex items-center h-14 rounded-full bg-[#E2E9EA]">
                <Input
                  type="text"
                  placeholder="Busque por título, ISBN, código Sankhya..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="flex-1 h-full pl-5 pr-14 rounded-full bg-transparent border-0 text-[#151C1D] text-base placeholder:text-[#3B494A] focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={clearSearch}
                    className="absolute right-1 h-12 w-12 rounded-full hover:bg-transparent"
                  >
                    <X className="w-6 h-6 text-[#151C1D]" />
                  </Button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex gap-2">
                <button
                  onClick={() => toggleQuickFilter("editorialPending")}
                  className={`h-8 px-4 rounded-lg border flex items-center transition-colors ${
                    filters.editorialPending
                      ? "border-[#00646B] bg-[#E0F2F1] text-[#00646B]"
                      : "border-[#BAC9CA] bg-white text-[#3B494A] hover:bg-[#EDF4F5]"
                  }`}
                >
                  Editorial Pendente
                </button>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setIsFilterModalOpen(true)}
                  className="h-12 px-3 gap-1 rounded-xl bg-[#EDF4F5] shadow-sm relative"
                >
                  <Filter className="w-5 h-5 text-[#00646B]" />
                  <span className="text-[#00646B] font-medium">Filtrar</span>

                  {hasActiveFilters() && (
                    <span className="absolute -top-2 -right-2 min-w-6 h-6 rounded-full bg-[#BA1A1A] text-white text-xs font-bold flex items-center justify-center">
                      {countActiveFilters()}
                    </span>
                  )}
                </Button>

                {hasActiveFilters() && (
                  <button
                    onClick={() => {
                      setFilters({
                        primaryCategories: [],
                        secondaryCategories: [],
                        materialidades: [],
                        csatMin: 0,
                        csatMax: 5,
                        costMin: 1,
                        costMax: 50,
                        contractType: [],
                        contractStatus: [],
                        simulationAptness: [],
                        contractPending: false,
                      costPending: false,
                        editorialPending: false,
                      expiration_from: "",
                      expiration_to: "",
                      });
                      setCurrentPage(1);
                    }}
                    className="text-[#00646B] text-sm font-normal hover:underline cursor-pointer bg-transparent border-none p-0 hover:bg-transparent"
                  >
                    Remover Filtros
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-[28px] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#EDF4F5] border-b border-[#BAC9CA] hover:bg-[#EDF4F5]">
                  <TableHead className="text-[#3B494A] font-semibold text-xs pl-6">
                    Título
                  </TableHead>
                  <TableHead className="text-[#3B494A] font-semibold text-xs">
                    ISBN
                  </TableHead>
                  <TableHead className="text-[#3B494A] font-semibold text-xs">
                    Código Sankhya
                  </TableHead>
                  <TableHead className="text-[#3B494A] font-semibold text-xs">
                    Categoria Primária
                  </TableHead>
                  <TableHead className="text-[#3B494A] font-semibold text-xs text-center">
                    Categoria Secundária
                  </TableHead>
                  <TableHead className="text-[#3B494A] font-semibold text-xs text-center">
                    Materialidade
                  </TableHead>
                  <TableHead className="text-[#3B494A] font-semibold text-xs text-center">
                    Status
                  </TableHead>
                  <TableHead className="text-[#3B494A] font-semibold text-xs text-center">
                    Apto para Simulação?
                  </TableHead>
                  <TableHead className="w-6"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="h-32 text-center text-[#3B494A]"
                    >
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : isError ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="h-32 text-center text-[#93000A]"
                    >
                      {error?.message ?? "Erro ao carregar livros."}
                    </TableCell>
                  </TableRow>
                ) : currentBooks.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="h-32 text-center text-[#3B494A]"
                    >
                      Nenhum livro encontrado
                      {searchQuery ? ` para "${searchQuery}"` : ""}.
                    </TableCell>
                  </TableRow>
                ) : (
                  currentBooks.map((book) => (
                    <TableRow
                      key={book.id}
                      onClick={() => navigate(`/catalog/${book.id}`)}
                      className="border-b border-[#BAC9CA] cursor-pointer"
                    >
                      <TableCell className="font-medium pl-6">
                        <div className="max-w-[300px] truncate text-sm" title={book.title}>
                          <HighlightedText text={book.title} highlight={searchQuery} />
                        </div>
                      </TableCell>
                      <TableCell className="text-[#3B494A] text-sm max-w-[140px] truncate">
                        {book.isbn ?? "—"}
                      </TableCell>
                      <TableCell className="text-[#3B494A] text-sm max-w-[120px] truncate">
                        {book.sankhya_code ?? "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(book.primary_categories ?? []).map((cat) => (
                            <Badge key={cat.id} className="h-6 px-2 rounded bg-[#D6E5E6] text-[#3B494A] text-xs font-semibold">
                              {cat.code}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-center align-middle">
                        <div className="flex flex-wrap gap-1 justify-center">
                          {(book.secondary_categories ?? []).length === 0 ? (
                            <span className="text-[#3B494A] text-sm">-</span>
                          ) : (
                            (book.secondary_categories ?? []).map((cat) => (
                              <Badge key={cat.id} className="h-6 px-2 rounded bg-[#EDF4F5] text-[#3B494A] text-xs font-semibold">
                                {cat.code}
                              </Badge>
                            ))
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-[#3B494A] w-40 uppercase text-center align-middle">
                        {book.materiality?.name ?? "—"}
                      </TableCell>
                      <TableCell className="text-center align-middle">
                        <Badge
                          className={`${getBookStatusBadgeClass(book.status)} uppercase`}
                        >
                          {getBookStatusLabel(book.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center align-middle">
                        <Badge
                          className={`${getEligibilityBadgeClass(book.eligible_for_simulation)} uppercase`}
                        >
                          {getEligibilityLabel(book.eligible_for_simulation)}
                        </Badge>
                      </TableCell>

                      <TableCell onClick={(e) => e.stopPropagation()} className="w-6 p-0">
                        <DropdownMenu
                          open={selectedBook === String(book.id)}
                          onOpenChange={(open) =>
                            setSelectedBook(open ? String(book.id) : null)
                          }
                        >
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 rounded-sm hover:bg-[#0000000D] p-0"
                            >
                              <MoreVertical className="h-2 w-2 text-[#3B494A]" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-[220px] bg-[#E8EFF0] rounded"
                          >
                            <DropdownMenuItem
                              className="h-10 px-2 gap-2 bg-[#B4EEDF] text-[#366E62] focus:bg-[#B4EEDF] focus:text-[#366E62]"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedBook(null);
                                navigate(`/catalog/${book.id}/editar`);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                              <span className="flex-1 text-sm">Editar livro</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="h-10 px-2 gap-2"
                              disabled={toggleStatusMutation.isPending}
                              onClick={(e) => handleToggleStatus(e, book.id)}
                            >
                              <ToggleLeft className="w-4 h-4" />
                              <span className="flex-1 text-sm text-[#151C1D]">
                                Alternar status
                              </span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="h-10 px-2 gap-2"
                              disabled={updateSimulationEligibilityMutation.isPending}
                              onClick={(e) => handleToggleSimulationEligibility(e, book)}
                            >
                              <FlaskConical className="w-4 h-4" />
                              <span className="flex-1 text-sm text-[#151C1D]">
                                {isAptoSimulacao(book) ? "Remover da simulação" : "Apto para simulação"}
                              </span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <div className="flex items-center justify-end gap-4 px-4 py-3 bg-[#EDF4F5] border-t border-[#BAC9CA]">
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#3B494A]">
                  Itens por página:
                </span>
                <Select
                  value={String(itemsPerPage)}
                  onValueChange={(v) => {
                    setItemsPerPage(Number(v));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 w-16 border-[#BAC9CA] rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="15">15</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <span className="text-xs text-[#3B494A]">
                {pagination
                  ? `${pagination.from ?? 0} a ${pagination.to ?? 0} de ${pagination.total}`
                  : "0 a 0 de 0"}
              </span>

              <div className="flex gap-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-12 w-12"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination || pagination.current_page <= 1}
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-12 w-12"
                  onClick={() =>
                    setCurrentPage((p) =>
                      Math.min(pagination?.last_page ?? 1, p + 1)
                    )
                  }
                  disabled={
                    !pagination ||
                    pagination.current_page >= (pagination.last_page ?? 1)
                  }
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
